import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
// Added metadata import
import { addDoc, collection, serverTimestamp, doc, onSnapshot, updateDoc, increment } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Payment from './payment'; 

const NAVY = '#001F3F';
const GOLD = '#B8860B';
const PROMO_FEE = 2000;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit for Storage Rules

export default function SignatureStylesUpload() {
  const [styleName, setStyleName] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  const [duration, setDuration] = useState('45');
  const [faceShape, setFaceShape] = useState('oval');
  const [isPromoted, setIsPromoted] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const [showPayment, setShowPayment] = useState(false);
  const [pendingStyleId, setPendingStyleId] = useState(null);

  const [unlockedAssets, setUnlockedAssets] = useState([]);
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);

  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const unsub = onSnapshot(doc(db, 'barbers', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setUnlockedAssets(data.purchasedAssets || []);
        setWalletBalance(data.walletBalance || 0);
      }
    });
    return () => unsub();
  }, [user, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        return toast.error("File exceeds 10MB limit.");
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        return toast.error("File exceeds 10MB limit.");
      }
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handlePaymentSuccess = async () => {
    if (!pendingStyleId) return;
    
    try {
      const styleRef = doc(db, 'global_gallery', pendingStyleId);
      await updateDoc(styleRef, {
        promotionStatus: 'pending_approval',
        isPromoted: false, 
        boostPaid: true,
        paidAt: serverTimestamp()
      });
      
      toast.success("Payment Verified! Your style is now in the Admin Approval Queue.");
      setShowPayment(false);
      navigate('/stylist-dashboard');
    } catch (err) {
      console.error("Status update failed:", err);
      toast.error("Payment recorded but status update failed. Contact support.");
    }
  };

  const handleUpload = async () => {
    if (!styleName.trim()) return toast.error('Please give your style a name');
    if (!servicePrice || isNaN(servicePrice)) return toast.error('Please set a valid price');
    if (!imageFile && !videoFile) return toast.error('Please upload media');

    setUploading(true);
    setError(null);

    try {
      let imageUrl = null;
      let videoUrl = null;

      // 1. Image Upload with Metadata
      if (imageFile) {
        const isGLB = imageFile.name.endsWith('.glb');
        const imgRef = storageRef(storage, `global_gallery/${Date.now()}-img${isGLB ? '.glb' : '.jpg'}`);
        
        // CRITICAL: Explicitly setting metadata for Security Rules validation
        const metadata = {
          contentType: isGLB ? 'model/gltf-binary' : 'image/jpeg'
        };

        const task = await uploadBytesResumable(imgRef, imageFile, metadata);
        imageUrl = await getDownloadURL(task.ref);
      }

      // 2. Video Upload with Metadata
      if (videoFile) {
        const vidRef = storageRef(storage, `global_gallery/${Date.now()}-vid.mp4`);
        
        const metadata = {
          contentType: 'video/mp4'
        };

        const task = await uploadBytesResumable(vidRef, videoFile, metadata);
        videoUrl = await getDownloadURL(task.ref);
      }

      const styleData = {
        name: styleName.trim().toLowerCase(),
        price: Number(servicePrice),
        duration: Number(duration),
        imageUrl: imageUrl,
        videoUrl: videoUrl,
        modelUrl: imageFile?.name.endsWith('.glb') ? imageUrl : null, 
        creatorId: user.uid,
        creatorName: user.displayName || 'Verified Barber',
        creatorEmail: user.email,
        idealFor: faceShape,
        promotionStatus: 'none', 
        isPromoted: false, 
        votesCount: 0,
        voters: [],
        isSignature: true,
        applied3DAsset: selectedTexture, 
        createdAt: serverTimestamp(),
        tags: [styleName.toLowerCase(), faceShape, 'standard']
      };

      const docRef = await addDoc(collection(db, 'global_gallery'), styleData);

      // Duplicating to portfolio for stylist quick-view
      await addDoc(collection(db, 'barbers', user.uid, 'portfolio'), {
        styleId: docRef.id,
        imageUrl,
        name: styleName.trim().toLowerCase(),
        price: Number(servicePrice),
        createdAt: serverTimestamp()
      });

      if (isPromoted) {
          setPendingStyleId(docRef.id);
          setShowPayment(true);
          setUploading(false); 
      } else {
          toast.success("Style Published to Vault!");
          setTimeout(() => navigate('/stylist-dashboard'), 2000);
      }
    } catch (err) {
      console.error(err);
      setError('Upload failed – check connection or permissions');
      setUploading(false);
    }
  };

  return (
    <div style={layoutContainer}>
      <div style={sidebarStyle}>
        <h3 style={{ color: GOLD, fontSize: '1rem', marginBottom: '20px', letterSpacing: '1px' }}>3D ASSET TAGGING</h3>
        {unlockedAssets.length === 0 ? (
          <p style={{ fontSize: '0.8rem', opacity: 0.5 }}>No 3D textures found.</p>
        ) : (
          <div style={assetGrid}>
            {unlockedAssets.map((url, idx) => (
              <div key={idx} onClick={() => setSelectedTexture(url)}
                style={{...thumbStyle, border: selectedTexture === url ? `3px solid ${GOLD}` : '1px solid rgba(255,255,255,0.1)'}}>
                <img src={url} style={thumbImg} alt="Texture" />
                {selectedTexture === url && <div style={activeOverlay}>TAGGED</div>}
              </div>
            ))}
          </div>
        )}
        {selectedTexture && <button onClick={() => setSelectedTexture(null)} style={clearAssetBtn}>Remove 3D Tag</button>}
        
        <div style={{ marginTop: '40px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
             <p style={{ fontSize: '0.7rem', color: GOLD }}>WALLET BALANCE</p>
             <h2 style={{ color: 'white' }}>₦{walletBalance.toLocaleString()}</h2>
             <button onClick={() => navigate('/wallet')} style={clearAssetBtn}>Top Up Balance</button>
        </div>
      </div>

      <div style={mainContentStyle}>
        <div style={formWrapper}>
          <img src="/icons/esdras-logo.png" alt="logo" style={{ width: '80px', marginBottom: '1rem' }} />
          <h1 style={titleStyle}>Publish Masterpiece</h1>

          {error && <div style={errorBox}>{error}</div>}

          <input type="text" placeholder="Style name" value={styleName}
            onChange={(e) => setStyleName(e.target.value)} style={inputStyle} />

          <div style={pricingRow}>
            <div style={{ flex: 1 }}>
                <label style={miniLabel}>SERVICE PRICE (₦)</label>
                <input type="number" placeholder="5000" value={servicePrice}
                    onChange={(e) => setServicePrice(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
                <label style={miniLabel}>DURATION (MINS)</label>
                <select style={inputStyle} value={duration} onChange={(e) => setDuration(e.target.value)}>
                    <option value="15">15 Mins</option>
                    <option value="30">30 Mins</option>
                    <option value="45">45 Mins</option>
                    <option value="60">60 Mins</option>
                </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '15px', marginBottom: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>FACE SHAPE</label>
                <select style={inputStyle} onChange={(e) => setFaceShape(e.target.value)}>
                    <option value="oval">Oval</option>
                    <option value="square">Square</option>
                    <option value="round">Round</option>
                </select>
              </div>
              
              <div style={promoToggleContainer} onClick={() => setIsPromoted(!isPromoted)}>
                <label style={miniLabel}>BOOST TO RUNWAY (₦{PROMO_FEE})</label>
                <div style={{...toggleBase, background: isPromoted ? GOLD : '#111'}}>
                    <div style={{...toggleCircle, transform: isPromoted ? 'translateX(20px)' : 'translateX(0px)'}} />
                </div>
              </div>
          </div>

          <div style={uploadRow}>
            <div style={uploadBox}>
              <label style={uploadLabel}>
                <input type="file" accept="image/*,.glb" onChange={handleImageChange} style={{ display: 'none' }} />
                📸 Photo / .GLB
              </label>
              {imagePreview && <img src={imagePreview} style={previewMedia} alt="Preview" />}
            </div>
            <div style={uploadBox}>
              <label style={uploadLabel}>
                <input type="file" accept="video/*" onChange={handleVideoChange} style={{ display: 'none' }} />
                🎥 Reel Upload
              </label>
              {videoPreview && <video src={videoPreview} style={previewMedia} controls />}
            </div>
          </div>

          <button onClick={handleUpload} disabled={uploading} style={submitBtnStyle}>
            {uploading ? 'UPLOADING...' : isPromoted ? 'CONTINUE TO PAYMENT' : 'PUBLISH TO VAULT'}
          </button>
          
          <p style={chatNote}>✅ Style will appear in Global Discovery after upload.</p>
        </div>
      </div>

      {showPayment && (
        <Payment 
          bookingId={user.uid}
          amount={PROMO_FEE}
          email={user.email}
          isBoost={true}
          styleId={pendingStyleId}
          onSuccess={handlePaymentSuccess}
          onClose={() => {
            setShowPayment(false);
            navigate('/stylist-dashboard'); 
          }}
        />
      )}
    </div>
  );
}

// ... (Rest of your styles remain unchanged)
const layoutContainer = { display: 'flex', minHeight: '100vh', background: NAVY, color: 'white' };
const sidebarStyle = { width: '280px', background: 'rgba(0,0,0,0.4)', padding: '2rem', borderRight: `1px solid rgba(255,255,255,0.1)` };
const mainContentStyle = { flex: 1, display: 'grid', placeItems: 'center', padding: '2rem' };
const formWrapper = { width: '100%', maxWidth: '600px', textAlign: 'center', background: 'rgba(255,255,255,0.03)', padding: '3rem', borderRadius: '30px', border: `1px solid rgba(184, 134, 11, 0.2)` };
const titleStyle = { color: GOLD, fontSize: '2rem', fontWeight: '800', marginBottom: '1.5rem' };
const inputStyle = { width: '100%', padding: '1.2rem', borderRadius: '15px', background: '#000', border: `1px solid rgba(184,134,11,0.3)`, color: 'white', outline: 'none' };
const pricingRow = { display: 'flex', gap: '15px', margin: '15px 0', textAlign: 'left' };
const miniLabel = { fontSize: '0.6rem', color: GOLD, fontWeight: 'bold', marginLeft: '5px', marginBottom: '5px', display: 'block', letterSpacing: '1px' };
const uploadRow = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '2rem' };
const uploadBox = { background: 'rgba(255,255,255,0.05)', borderRadius: '20px', padding: '15px', border: '1px dashed rgba(255,255,255,0.2)' };
const uploadLabel = { cursor: 'pointer', fontSize: '0.9rem', color: GOLD, fontWeight: 'bold' };
const previewMedia = { width: '100%', borderRadius: '12px', marginTop: '10px', height: '100px', objectFit: 'cover' };
const submitBtnStyle = { width: '100%', background: GOLD, color: 'black', padding: '1.2rem', borderRadius: '50px', fontWeight: 'bold', border: 'none', cursor: 'pointer' };
const errorBox = { color: '#ff6b6b', marginBottom: '1rem' };
const assetGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
const thumbStyle = { height: '80px', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', position: 'relative' };
const thumbImg = { width: '100%', height: '100%', objectFit: 'cover' };
const activeOverlay = { position: 'absolute', inset: 0, background: 'rgba(184, 134, 11, 0.7)', display: 'grid', placeItems: 'center', fontSize: '10px', color: 'black' };
const clearAssetBtn = { marginTop: '15px', background: 'none', border: `1px solid #ff6b6b`, color: '#ff6b6b', padding: '5px', borderRadius: '5px', width: '100%' };
const promoToggleContainer = { display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' };
const toggleBase = { width: '45px', height: '24px', borderRadius: '20px', padding: '2px', transition: '0.3s' };
const toggleCircle = { width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: '0.3s' };
const chatNote = { fontSize: '0.65rem', opacity: 0.4, marginTop: '20px', letterSpacing: '1px' };
